import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Paper,
  Divider,
  IconButton,
  InputAdornment,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio
} from '@mui/material';
import {
  PersonAdd,
  Email,
  Lock,
  Person,
  Close,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import Cookies from 'js-cookie';
import { config } from '../config';

const CommenterRegistrationForm = ({ onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    gender: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Clear error when user starts typing
    if (error) setError('');
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('Username is required');
      return false;
    }
    if (!formData.email.trim()) {
      setError('Email is required');
      return false;
    }
    if (!formData.password) {
      setError('Password is required');
      return false;
    }
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    if (!formData.gender) {
      setError('Please select your gender');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      const API_BASE_URL = config.API_URL;
      const response = await fetch(`${API_BASE_URL}/auth/signup-commenter`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim(),
          password: formData.password,
          gender: formData.gender
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Registration failed');
      }

      // Store the token and user data
      localStorage.setItem('auth_token', data.token);
      Cookies.set('auth_token', data.token, { expires: 7 }); // Also set in cookies
      
      // Call success callback with user data
      onSuccess(data.user);
    } catch (err) {
      console.error('Registration error:', err);
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        maxWidth: '400px',
        width: '100%',
        mx: 'auto'
      }}
    >
      {/* Header */}
      <Box
        sx={{
          backgroundColor: '#f8f9fa',
          p: 2,
          borderBottom: '1px solid #e9ecef',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#212529' }}>
          Create an account to Join
        </Typography>
        <IconButton 
          onClick={onCancel} 
          size="small"
          sx={{ 
            color: '#6c757d',
            '&:hover': { backgroundColor: '#e9ecef' }
          }}
        >
          <Close />
        </IconButton>
      </Box>

      {/* Form Content */}
      <Box sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          {/* Username Field */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ color: '#495057', mb: 1, fontWeight: 500 }}>
              Username
            </Typography>
            <TextField
              fullWidth
              name="name"
              placeholder="Enter your username"
              value={formData.name}
              onChange={handleChange}
              disabled={loading}
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  backgroundColor: '#ffffff',
                  '& fieldset': {
                    borderColor: '#dee2e6',
                    borderWidth: '1px'
                  },
                  '&:hover fieldset': {
                    borderColor: '#adb5bd'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#007bff',
                    borderWidth: '2px'
                  }
                },
                '& .MuiInputBase-input': {
                  py: 1.5,
                  fontSize: '16px'
                }
              }}
            />
          </Box>

          {/* Email Field */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ color: '#495057', mb: 1, fontWeight: 500 }}>
              Email
            </Typography>
            <TextField
              fullWidth
              name="email"
              type="email"
              placeholder="Enter your email"
              value={formData.email}
              onChange={handleChange}
              disabled={loading}
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  backgroundColor: '#ffffff',
                  '& fieldset': {
                    borderColor: '#dee2e6',
                    borderWidth: '1px'
                  },
                  '&:hover fieldset': {
                    borderColor: '#adb5bd'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#007bff',
                    borderWidth: '2px'
                  }
                },
                '& .MuiInputBase-input': {
                  py: 1.5,
                  fontSize: '16px'
                }
              }}
            />
          </Box>

          {/* Password Field */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ color: '#495057', mb: 1, fontWeight: 500 }}>
              Password
            </Typography>
            <TextField
              fullWidth
              name="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Enter your password"
              value={formData.password}
              onChange={handleChange}
              disabled={loading}
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                      sx={{ color: '#6c757d' }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  backgroundColor: '#ffffff',
                  '& fieldset': {
                    borderColor: '#dee2e6',
                    borderWidth: '1px'
                  },
                  '&:hover fieldset': {
                    borderColor: '#adb5bd'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#007bff',
                    borderWidth: '2px'
                  }
                },
                '& .MuiInputBase-input': {
                  py: 1.5,
                  fontSize: '16px'
                }
              }}
            />
          </Box>

          {/* Confirm Password Field */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ color: '#495057', mb: 1, fontWeight: 500 }}>
              Confirm Password
            </Typography>
            <TextField
              fullWidth
              name="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="Confirm your password"
              value={formData.confirmPassword}
              onChange={handleChange}
              disabled={loading}
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                      size="small"
                      sx={{ color: '#6c757d' }}
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  backgroundColor: '#ffffff',
                  '& fieldset': {
                    borderColor: '#dee2e6',
                    borderWidth: '1px'
                  },
                  '&:hover fieldset': {
                    borderColor: '#adb5bd'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#007bff',
                    borderWidth: '2px'
                  }
                },
                '& .MuiInputBase-input': {
                  py: 1.5,
                  fontSize: '16px'
                }
              }}
            />
          </Box>

          {/* Gender Selection */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ color: '#495057', mb: 1, fontWeight: 500 }}>
              Gender
            </Typography>
            <FormControl component="fieldset" sx={{ width: '100%' }}>
              <RadioGroup
                row
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                sx={{
                  justifyContent: 'space-around',
                  '& .MuiFormControlLabel-root': {
                    margin: 0,
                    '& .MuiRadio-root': {
                      color: '#6c757d',
                      '&.Mui-checked': {
                        color: '#007bff'
                      }
                    },
                    '& .MuiFormControlLabel-label': {
                      fontSize: '14px',
                      color: '#495057',
                      fontWeight: 500
                    }
                  }
                }}
              >
                <FormControlLabel 
                  value="male" 
                  control={<Radio />} 
                  label="Male" 
                  disabled={loading}
                />
                <FormControlLabel 
                  value="female" 
                  control={<Radio />} 
                  label="Female" 
                  disabled={loading}
                />
              </RadioGroup>
            </FormControl>
          </Box>

          {/* Create Account Button */}
          <Button
            fullWidth
            variant="contained"
            type="submit"
            disabled={loading}
            sx={{
              backgroundColor: '#007bff',
              borderRadius: '8px',
              py: 1.5,
              fontSize: '16px',
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: '0 2px 4px rgba(0,123,255,0.2)',
              '&:hover': {
                backgroundColor: '#0056b3',
                boxShadow: '0 4px 8px rgba(0,123,255,0.3)'
              },
              '&:disabled': {
                backgroundColor: '#6c757d',
                boxShadow: 'none'
              }
            }}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Create Account'
            )}
          </Button>
        </Box>

        {/* Footer Text */}
        <Typography variant="caption" sx={{ 
          color: '#6c757d', 
          textAlign: 'center', 
          display: 'block',
          mt: 2,
          lineHeight: 1.4
        }}>
          By creating an account, you agree to participate respectfully in discussions.
          A random profile icon will be assigned based on your gender selection.
        </Typography>
      </Box>
    </Box>
  );
};

export default CommenterRegistrationForm;
